// src/components/crm/QuotePipelineBoard.js
// SEAM 8b VERIFIER: qboard-7e3c9b42
//
// The estimates BOARD: one column per pipeline stage, in the org's own order,
// with the estimates as draggable cards. The counterpart to the table on the
// same screen (QuotesView owns the view toggle).
//
// WHY THIS ADDS NO QUERY. Every field a card shows is already on the row
// objects QuotesView builds — it reads the whole quotes collection live
// (QuotesView.js:45-88) and already attaches `attention` per row via
// resolveQuoteAttention. This component is handed that array. It opens no
// listener, reads no subcollection, and needs no composite index. Do not add a
// query here: the "last touched including notes" figure that would need one is
// deliberately out of scope (see BACKLOG 8b).
//
// MOVES GO THROUGH ONE DOOR. setQuotePipelineStage, exactly as the estimate
// page's picker does (QuotePipelineStagePicker.js:58) — firestore.rules denies
// client writes to pipelineStageId. A stage write can never touch status:
// PIPELINE_QUOTE_FIELDS is frozen to two fields and assertStageUpdateShape
// requires that exact key set (functions/utils/quotePipeline.js:68,139).
//
// NO CLIENT MIRROR OF allowedManualTargets, ON PURPOSE. Every column accepts
// the drop and the SERVER decides. Predicting the refusal here would mean a
// second copy of that rule and its wording, drifting the first time the rule
// changes; the cost of not predicting is one wasted call on a rare user error,
// and the payoff is that the sentence the user reads is always the server's.
//
// INTERNAL ONLY: stage labels like "Lost" must never reach a customer's
// browser. Never mount this in a portal view.
//
// qclose-3e8a5c17 — Seam 9b adds two things:
//   * A drop the SERVER reports as a close (result.close === 'close') opens the
//     close question, pinned to the close it just recorded. A refused drop
//     never does.
//   * A refusal can no longer go unseen. It used to render only under the
//     snapped-back card — and dragging from a far-right column (Lost) to a
//     far-left one (Sent) auto-scrolls the board away from that card, so the
//     server's sentence could appear entirely off-screen (founder round,
//     2026-10-01: E-0018 Lost → Sent "was refused" with no visible reason).
//     The same words now ALSO show in a banner above the board, and the card is
//     scrolled back into view.
import React, { useState, useMemo, useEffect, useRef, useCallback } from 'react';
import { httpsCallable } from 'firebase/functions';
import { AlertTriangle, Lock, Clock, ChevronDown, ChevronRight, X, ArrowRight, Eye } from 'lucide-react';
import { functions } from '../../firebase/config';
import { formatCurrency, formatDate } from '../../utils/formatters';
import { can, PERMISSIONS } from '../../utils/permissions';
import QuoteStatusBadge from '../common/QuoteStatusBadge';
import CloseDetailsModal from './CloseDetailsModal';                 // qclose-3e8a5c17
import useQuoteVocabularies from '../../hooks/useQuoteVocabularies'; // qclose-3e8a5c17
import {
    findPipelineStage,
    deriveSystemStageId,
    resolveQuoteSalesPerson,
    quoteAttentionLabel,
    toExpiryDate,
    ymdInTimeZone,
    calendarDaysBetween,
} from '../../utils/quoteSchema';
import { groupQuotesByStage } from '../../utils/quoteReporting'; // qreport-5b8e2c64

// How many cards a closed column reveals per click — first "Show 63", then
// "Show 20 more", repeatable until all of them are on screen. The header still
// counts and totals EVERY card in the column, so the number beside the label
// never disagrees with reality just because the body is showing fewer.
const CLOSED_PAGE = 20;

// qboard-7e3c9b42-autoscroll — native HTML5 drag does NOT auto-scroll a
// scroll container (this is the drag-and-drop API's own gap, not a bug in the
// browser), so a column off the right-hand edge was literally unreachable: the
// founder round had to zoom the page out to ~20% to drop a card. These drive a
// requestAnimationFrame loop while a drag is in flight.
//   EDGE   — how close to an edge the pointer has to get before it scrolls.
//   MAX    — pixels per frame at the very edge; it eases in from 0 at EDGE, so
//            approaching slowly nudges and pinning to the edge races.
const AUTOSCROLL_EDGE = 80;
const AUTOSCROLL_MAX = 22;

// Column body height. An expanded column scrolls WITHIN ITSELF rather than
// stretching the page to the height of the longest column — 62 cards in Won
// would otherwise push every other column's header off the top of the screen.
const COLUMN_BODY_MAX = 'max-h-[60vh]';

const COLUMN_TINT = {
    open:     'bg-blue-50/60 border-blue-100',
    won:      'bg-green-50/60 border-green-100',
    lost:     'bg-red-50/60 border-red-100',
    deferred: 'bg-amber-50/60 border-amber-100',
};

// qboard-7e3c9b42-reason — why a card moved on its own. The keys are the `rule`
// values deriveSystemStageId returns (src/utils/quoteSchema.js:618-639); an
// unlisted rule falls back to no explanation rather than inventing one.
const AUTO_MOVE_PHRASE = {
    converted: 'converted to a job',
    approved: 'approved',
    awaiting_deposit: 'deposit owing',
    declined: 'declined',
    changes_requested: 'changes requested',
    sent: 'sent',
    draft: 'back to draft',
    // qview-6b2d4e91 — NOT a deriveSystemStageId rule (status never reaches
    // Viewed; only the Seam 10 view mover does). Looked up by the target stage
    // id below, so a Sent → Viewed move doesn't read "— sent".
    viewed: 'the customer opened it',
};

// Calendar days the estimate has sat in its stage, counted in the ORG's
// timezone the same way every other Seam 5/7/8 day count is.
const daysInStage = (quote, timeZone, todayYmd) => {
    const setAt = toExpiryDate(quote.pipelineStageSetAt);
    if (!setAt) return null;
    return calendarDaysBetween(ymdInTimeZone(setAt, timeZone), todayYmd);
};

const QuotePipelineBoard = ({
    orgId,
    stages,
    quotes,
    users,
    currentUserProfile,
    companySettings,
    term,
    onQuoteSelect,
}) => {
    // Optimistic placement: quoteId -> stageId we have asked the server for.
    // Cleared when the live snapshot catches up, or immediately on refusal.
    const [pendingMoves, setPendingMoves] = useState({});
    const [dragOverStageId, setDragOverStageId] = useState(null);
    // qboard-7e3c9b42-reveal — stageId -> HOW MANY cards that closed column is
    // showing. Absent or 0 means collapsed. A count rather than a boolean is
    // what lets "Show 20 more" be repeatable: the first reveal sets CLOSED_PAGE,
    // each further one adds another page, and collapsing sets it back to 0.
    const [revealed, setRevealed] = useState({});
    const [refusal, setRefusal] = useState(null);   // { quoteId, quoteNumber, message }
    // qclose-3e8a5c17 — the close question after a drop that closed an estimate.
    const [closePrompt, setClosePrompt] = useState(null); // { quote, closeActivityId, outcome }
    const vocabularies = useQuoteVocabularies(orgId);
    const [autoNotes, setAutoNotes] = useState([]); // [{ key, text }]

    const draggingRef = useRef(null);               // { quoteId, fromStageId }
    const prevStageRef = useRef(null);              // Map quoteId -> stageId
    const ownMoveRef = useRef(new Set());           // `${quoteId}:${stageId}` we caused
    const scrollerRef = useRef(null);               // the horizontal strip of columns
    // qboard-7e3c9b42-autoscroll — live auto-scroll state, held in a ref so the
    // rAF loop reads the latest without re-rendering on every pointer move.
    const autoScrollRef = useRef({ raf: 0, hVel: 0, vEl: null, vVel: 0 });

    const timeZone = companySettings?.timezone;
    const canMove = can(currentUserProfile, PERMISSIONS.MANAGE_FINANCIALS);

    const todayYmd = useMemo(
        () => ymdInTimeZone(new Date(), timeZone),
        [timeZone]
    );

    const userNameById = useMemo(() => {
        const map = new Map();
        for (const u of users || []) map.set(u.id, u.name || u.email || null);
        return map;
    }, [users]);

    // --- clear an optimistic placement once the snapshot agrees with it ------
    useEffect(() => {
        setPendingMoves((prev) => {
            const ids = Object.keys(prev);
            if (!ids.length) return prev;
            const next = { ...prev };
            let changed = false;
            for (const qid of ids) {
                const q = quotes.find((x) => x.id === qid);
                // Card gone (filtered out or deleted) — stop tracking it.
                if (!q) { delete next[qid]; changed = true; continue; }
                if ((q.pipelineStageId || null) === prev[qid]) { delete next[qid]; changed = true; }
            }
            return changed ? next : prev;
        });
    }, [quotes]);

    // --- announce a card that moved itself ----------------------------------
    // A stage change we did NOT cause means the automatic mapping moved it:
    // onQuotePipelineSync reacts to a status change or to conversion, and those
    // overwrite a manual placement by design (onlyIfUnstaged is false for every
    // status rule). On the estimate page that is invisible; on a board the card
    // visibly moves, so it has to say why.
    //
    // HONEST LIMIT: this only catches moves that happen while the board is
    // open, and only for cards the current filters are showing. A move that
    // happened while you were elsewhere just appears. Catching those would mean
    // reading the activity subcollection — out of scope for this seam.
    useEffect(() => {
        const next = new Map();
        for (const q of quotes) next.set(q.id, q.pipelineStageId || null);

        const prev = prevStageRef.current;
        if (prev) {
            const fresh = [];
            for (const [qid, stageId] of next) {
                if (!prev.has(qid)) continue;              // newly visible, not a move
                if (prev.get(qid) === stageId) continue;

                const key = `${qid}:${stageId}`;
                if (ownMoveRef.current.has(key)) {         // our own drag landing
                    ownMoveRef.current.delete(key);
                    continue;
                }
                const quote = quotes.find((x) => x.id === qid);
                const stage = findPipelineStage(stageId, stages);
                if (!quote || !stage) continue;

                const phrase = stageId === 'viewed'
                    ? AUTO_MOVE_PHRASE.viewed
                    : AUTO_MOVE_PHRASE[deriveSystemStageId(quote, stages).rule];
                fresh.push({
                    key: `${key}:${Date.now()}`,
                    text: `${quote.quoteNumber || 'An estimate'} moved to ${stage.label}${phrase ? ` — ${phrase}` : ''}`,
                });
            }
            if (fresh.length) setAutoNotes((notes) => [...fresh, ...notes].slice(0, 4));
        }
        prevStageRef.current = next;
    }, [quotes, stages]);

    // --- the move -----------------------------------------------------------
    const runMove = useCallback(async (quote, stageId) => {
        setRefusal(null);
        setPendingMoves((prev) => ({ ...prev, [quote.id]: stageId }));
        ownMoveRef.current.add(`${quote.id}:${stageId}`);
        try {
            const res = await httpsCallable(functions, 'setQuotePipelineStage')({ quoteId: quote.id, stageId });
            // qclose-3e8a5c17 — the server, not this component, decides what a
            // close is (stage types; a known from-stage). Ask only when it says so.
            if (res?.data?.close === 'close') {
                setClosePrompt({ quote, closeActivityId: res.data.closeActivityId || null, outcome: res.data.outcome || null });
            }
        } catch (err) {
            console.error('QuotePipelineBoard: move failed', err);
            ownMoveRef.current.delete(`${quote.id}:${stageId}`);
            // Snap back: drop the optimistic placement so the card returns to
            // the column the server still has it in.
            setPendingMoves((prev) => {
                const next = { ...prev };
                delete next[quote.id];
                return next;
            });
            setRefusal({
                quoteId: quote.id,
                quoteNumber: quote.quoteNumber || null,
                message: err?.message || 'Could not move this estimate. Please try again.',
            });
            // qclose-3e8a5c17 — bring the snapped-back card (and the sentence
            // under it) back on screen; the drag may have scrolled far away.
            requestAnimationFrame(() => {
                const el = document.querySelector(`[data-board-card="${quote.id}"]`);
                if (el && typeof el.scrollIntoView === 'function') {
                    el.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' });
                }
            });
        }
    }, []);

    // --- auto-scroll while dragging ----------------------------------------
    // One rAF loop drives BOTH axes: the column strip horizontally, and
    // whichever thing is scrollable under the pointer vertically (the hovered
    // column's own body if it has overflow, otherwise the page).
    const stopAutoScroll = useCallback(() => {
        const s = autoScrollRef.current;
        if (s.raf) cancelAnimationFrame(s.raf);
        s.raf = 0; s.hVel = 0; s.vVel = 0; s.vEl = null;
    }, []);

    const tickAutoScroll = useCallback(() => {
        const s = autoScrollRef.current;
        if (!s.hVel && !s.vVel) { s.raf = 0; return; }
        if (s.hVel && scrollerRef.current) scrollerRef.current.scrollLeft += s.hVel;
        if (s.vVel) {
            if (s.vEl) s.vEl.scrollTop += s.vVel;
            else window.scrollBy(0, s.vVel);
        }
        s.raf = requestAnimationFrame(tickAutoScroll);
    }, []);

    // Distance-eased speed: 0 at the edge zone's inner boundary, MAX at the edge
    // itself. Returns a signed px/frame, or 0 when the pointer is clear of both
    // edges or that direction has nowhere left to go.
    const edgeVelocity = (pos, min, max, canBack, canFwd) => {
        const fromStart = pos - min;
        const fromEnd = max - pos;
        if (fromStart < AUTOSCROLL_EDGE && canBack) {
            return -AUTOSCROLL_MAX * (1 - Math.max(fromStart, 0) / AUTOSCROLL_EDGE);
        }
        if (fromEnd < AUTOSCROLL_EDGE && canFwd) {
            return AUTOSCROLL_MAX * (1 - Math.max(fromEnd, 0) / AUTOSCROLL_EDGE);
        }
        return 0;
    };

    const handleBoardDragOver = (e) => {
        if (!draggingRef.current) return;
        const s = autoScrollRef.current;

        // Horizontal: the strip of columns.
        const strip = scrollerRef.current;
        if (strip) {
            const r = strip.getBoundingClientRect();
            const maxLeft = strip.scrollWidth - strip.clientWidth;
            s.hVel = edgeVelocity(e.clientX, r.left, r.right, strip.scrollLeft > 0, strip.scrollLeft < maxLeft - 1);
        } else {
            s.hVel = 0;
        }

        // Vertical: the hovered column body if it scrolls, else the page.
        const col = e.target?.closest?.('[data-board-col-body]');
        if (col && col.scrollHeight > col.clientHeight + 1) {
            const r = col.getBoundingClientRect();
            const maxTop = col.scrollHeight - col.clientHeight;
            s.vEl = col;
            s.vVel = edgeVelocity(e.clientY, r.top, r.bottom, col.scrollTop > 0, col.scrollTop < maxTop - 1);
        } else {
            const doc = document.documentElement;
            const maxY = doc.scrollHeight - window.innerHeight;
            s.vEl = null;
            s.vVel = edgeVelocity(e.clientY, 0, window.innerHeight, window.scrollY > 0, window.scrollY < maxY - 1);
        }

        if ((s.hVel || s.vVel) && !s.raf) s.raf = requestAnimationFrame(tickAutoScroll);
    };

    // Belt and braces: a drag can end in ways this component never sees (Escape,
    // a drop outside the window), and a runaway scroll loop would be far worse
    // than the bug it fixes. These fire no matter where the drag finished.
    useEffect(() => {
        const stop = () => { draggingRef.current = null; stopAutoScroll(); };
        window.addEventListener('dragend', stop);
        window.addEventListener('drop', stop);
        return () => {
            window.removeEventListener('dragend', stop);
            window.removeEventListener('drop', stop);
            stopAutoScroll();
        };
    }, [stopAutoScroll]);

    const handleDragStart = (quote, fromStageId) => (e) => {
        draggingRef.current = { quoteId: quote.id, fromStageId };
        // Some browsers refuse to start a drag without payload.
        try { e.dataTransfer.setData('text/plain', quote.id); } catch { /* ignore */ }
        e.dataTransfer.effectAllowed = 'move';
    };

    const handleDragEnd = () => {
        draggingRef.current = null;
        stopAutoScroll();
        setDragOverStageId(null);
    };

    // Every column accepts the drop — the server is the authority on whether the
    // move is allowed (see the header note).
    const handleDragOver = (stageId) => (e) => {
        if (!draggingRef.current) return;
        e.preventDefault();
        e.dataTransfer.dropEffect = 'move';
        if (dragOverStageId !== stageId) setDragOverStageId(stageId);
    };

    const handleDrop = (stageId) => (e) => {
        e.preventDefault();
        const drag = draggingRef.current;
        draggingRef.current = null;
        stopAutoScroll();
        setDragOverStageId(null);
        if (!drag || drag.fromStageId === stageId) return;
        const quote = quotes.find((q) => q.id === drag.quoteId);
        if (quote) runMove(quote, stageId);
    };

    // --- grouping -----------------------------------------------------------
    // The effective stage is the optimistic one where we have asked for a move,
    // so a dragged card stays where it was dropped while the call is in flight.
    const effectiveStageId = useCallback(
        (quote) => (pendingMoves[quote.id] !== undefined ? pendingMoves[quote.id] : (quote.pipelineStageId || null)),
        [pendingMoves]
    );

    // qreport-5b8e2c64 — the bucketing and the per-stage sums are THE shared
    // board rule in quoteReporting.js, so the Estimates report's open value can
    // never disagree with these column totals. usePipelineStages returns null
    // until its first snapshot, and this memo runs BEFORE the early return
    // below — groupQuotesByStage tolerates that itself.
    const { columns, unplaced, boardTotal } = useMemo(
        () => groupQuotesByStage(quotes, stages, effectiveStageId),
        [stages, quotes, effectiveStageId]
    );

    if (!orgId || !stages) return null;

    // --- one card -----------------------------------------------------------
    const renderCard = (quote, stageId) => {
        const attention = quote.attention;
        const flagged = attention?.level === 'attention';
        const days = daysInStage(quote, timeZone, todayYmd);
        const inFlight = pendingMoves[quote.id] !== undefined;
        const refused = refusal?.quoteId === quote.id;

        const salesId = resolveQuoteSalesPerson(quote);
        const salesName = salesId ? (userNameById.get(salesId) || 'Unknown') : 'Unassigned';

        // Seam 7's invariant: a snooze MOVES nextFollowUpAt to the wake date, so
        // the two are the same instant. Never print both.
        const snoozedUntil = attention?.snoozed ? toExpiryDate(quote.snoozedUntil) : null;
        const followUpAt = !attention?.snoozed ? toExpiryDate(quote.nextFollowUpAt) : null;

        return (
            <div key={quote.id} className="relative" data-board-card={quote.id}>
                <div
                    draggable={canMove && !inFlight}
                    onDragStart={handleDragStart(quote, stageId)}
                    onDragEnd={handleDragEnd}
                    onClick={() => onQuoteSelect(quote)}
                    className={`bg-white p-3 rounded-lg border shadow-sm text-xs cursor-pointer transition-shadow hover:shadow-md ${
                        refused ? 'border-red-300' : 'border-gray-200'
                    } ${inFlight ? 'opacity-60' : ''} ${canMove ? 'active:cursor-grabbing' : ''}`}
                >
                    <div className="flex items-start justify-between gap-2 mb-1">
                        <span className="font-bold text-gray-900">{quote.quoteNumber}</span>
                        <span className="font-semibold text-gray-900 whitespace-nowrap">{formatCurrency(quote.total)}</span>
                    </div>

                    <p className="text-gray-700 mb-2 truncate" title={quote.customerName || ''}>
                        {quote.customerName || 'Unknown customer'}
                    </p>

                    <div className="mb-2">
                        <QuoteStatusBadge
                            status={quote.status}
                            depositStatus={quote.depositStatus}
                            isConverted={!!quote.convertedToJobId}
                            expiryState={quote.expiryState}
                            isPartialApproval={quote.status?.toLowerCase() === 'approved' && Array.isArray(quote.approvedItemIds) && Array.isArray(quote.declinedItemIds) && quote.approvedItemIds.length > 0 && quote.declinedItemIds.length > 0}
                        />
                    </div>

                    {/* qboard-7e3c9b42 — the Seam 8 chip plus its sentence. The
                        card has room for the reason, so show it like the phone
                        card in the list does rather than the one-word chip. */}
                    {flagged && (
                        <div className="mb-2 flex items-start gap-1.5 text-[11px] font-medium text-amber-700 bg-amber-50 border border-amber-200 rounded-md px-2 py-1">
                            <AlertTriangle size={12} className="mt-px shrink-0" />
                            <span>
                                <span className="font-semibold">{quoteAttentionLabel(attention)}</span>
                                {attention.reasons?.[0] ? ` — ${attention.reasons[0]}` : ''}
                            </span>
                        </div>
                    )}

                    <div className="space-y-0.5 text-[11px] text-gray-500 border-t border-gray-100 pt-1.5">
                        {snoozedUntil && <p>Snoozed until {formatDate(snoozedUntil)}</p>}
                        {followUpAt && <p>Follow-up {formatDate(followUpAt)}</p>}
                        <p className="flex items-center gap-1">
                            <Clock size={10} className="shrink-0" />
                            {days === null ? 'Stage date unknown'
                                : days === 0 ? '0 days in stage'
                                : days === 1 ? '1 day in stage'
                                : `${days} days in stage`}
                        </p>
                        {/* qview-6b2d4e91 — Seam 10. quote.viewSummary is attached
                            by QuotesView from its ONE org listener; no query here. */}
                        {quote.viewSummary && (
                            <p className="flex items-center gap-1" title={quote.viewSummary.full}>
                                <Eye size={10} className="shrink-0" />
                                {quote.viewSummary.short}{quote.viewSummary.count > 1 ? ` · ${quote.viewSummary.count}×` : ''}
                            </p>
                        )}
                        <p className={salesId && userNameById.has(salesId) ? '' : 'italic'}>{salesName}</p>
                    </div>
                </div>

                {/* A refused drop explains itself on the card it snapped back to,
                    in the SERVER's own words, and offers the one action that is
                    always safe: open the estimate, where the status lives (and
                    where approval's deposit guards live too). */}
                {refused && (
                    <div className="mt-1 p-2 rounded-md bg-red-50 border border-red-200 text-[11px] text-red-700">
                        <div className="flex items-start justify-between gap-2">
                            <span>{refusal.message}</span>
                            <button
                                type="button"
                                onClick={(e) => { e.stopPropagation(); setRefusal(null); }}
                                className="shrink-0 text-red-400 hover:text-red-600"
                                aria-label="Dismiss"
                            >
                                <X size={12} />
                            </button>
                        </div>
                        <button
                            type="button"
                            onClick={(e) => { e.stopPropagation(); setRefusal(null); onQuoteSelect(quote); }}
                            className="mt-1 inline-flex items-center gap-1 font-semibold text-red-800 hover:underline"
                        >
                            Open {term.word.toLowerCase()} <ArrowRight size={11} />
                        </button>
                    </div>
                )}
            </div>
        );
    };

    // --- one column ---------------------------------------------------------
    const renderColumn = ({ stage, cards, count, total }) => {
        const closed = stage.type !== 'open';
        const shown = revealed[stage.id] || 0;
        const expanded = !closed || shown > 0;
        const isOver = dragOverStageId === stage.id;

        // Closed columns show the most recently moved first — what you just won
        // or lost is what you might still want to look at.
        const body = expanded && closed
            ? [...cards].sort((a, b) => {
                const av = toExpiryDate(a.pipelineStageSetAt);
                const bv = toExpiryDate(b.pipelineStageSetAt);
                return (bv ? bv.getTime() : 0) - (av ? av.getTime() : 0);
            }).slice(0, shown)
            : cards;

        const more = closed && expanded ? count - body.length : 0;

        // qboard-7e3c9b42-narrow — an EMPTY open column is still a full drop
        // target with its own header, just a narrower one. Ten of the thirteen
        // starter stages are empty in every org, so full-width empties were the
        // bulk of the horizontal scrolling the founder round had to fight.
        const width = !expanded ? 'w-52' : (count === 0 ? 'w-44' : 'w-72');

        return (
            <div
                key={stage.id}
                onDragOver={handleDragOver(stage.id)}
                onDragLeave={() => setDragOverStageId((s) => (s === stage.id ? null : s))}
                onDrop={handleDrop(stage.id)}
                className={`shrink-0 rounded-lg border p-2.5 transition-colors ${width} ${
                    isOver ? 'bg-blue-100 border-blue-400 ring-2 ring-blue-300' : COLUMN_TINT[stage.type] || COLUMN_TINT.open
                }`}
            >
                <div className="mb-2.5">
                    <div className="flex items-start gap-1">
                        {closed && (
                            <button
                                type="button"
                                onClick={() => setRevealed((m) => ({ ...m, [stage.id]: expanded ? 0 : CLOSED_PAGE }))}
                                className="mt-0.5 text-gray-500 hover:text-gray-800"
                                aria-label={expanded ? `Collapse ${stage.label}` : `Expand ${stage.label}`}
                            >
                                {expanded ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                            </button>
                        )}
                        <h3 className="flex-1 font-bold text-sm text-gray-800 leading-tight">{stage.label}</h3>
                        {/* An explicit way back. The chevron alone was not a
                            visible exit once a column was full of cards. */}
                        {closed && expanded && (
                            <button
                                type="button"
                                onClick={() => setRevealed((m) => ({ ...m, [stage.id]: 0 }))}
                                className="shrink-0 text-[10px] font-semibold text-gray-500 hover:text-gray-800 hover:underline"
                            >
                                Collapse
                            </button>
                        )}
                    </div>
                    {/* D1: count AND money, on every header, over EVERY card in
                        the column — not just the ones a paged body draws. */}
                    <p className="mt-0.5 text-[11px] font-semibold text-gray-500">
                        {count} · {formatCurrency(total)}
                    </p>
                    {/* D2(a): system stages are put there by the estimate's own
                        status, which is why a card can move itself and why some
                        drops are refused. Keyed off isSystem, never the label. */}
                    {/* qview-6b2d4e91 — Viewed is the one system stage status never
                        reaches: a customer's first portal view puts a card there. */}
                    {stage.isSystem && (
                        <p
                            className="mt-0.5 inline-flex items-center gap-1 text-[10px] text-gray-400"
                            title={stage.id === 'viewed'
                                ? 'Sent estimates move here automatically the first time the customer opens them in the portal.'
                                : 'Estimates land here because of their status. Change the status to move one in or out.'}
                        >
                            <Lock size={9} /> {stage.id === 'viewed' ? 'customer opened' : 'follows status'}
                        </p>
                    )}
                </div>

                {expanded ? (
                    <>
                        {/* data-board-col-body marks this as the thing the drag
                            auto-scroller should scroll vertically when the
                            pointer is over it. */}
                        <div
                            data-board-col-body=""
                            className={`space-y-2 min-h-[80px] overflow-y-auto overflow-x-hidden ${COLUMN_BODY_MAX} -mx-0.5 px-0.5`}
                        >
                            {body.map((q) => renderCard(q, stage.id))}
                            {count === 0 && (
                                <p className="text-center text-[11px] text-gray-400 pt-6">
                                    {/* qview-6b2d4e91 — nobody can drag into Viewed (the server
                                        refuses); only a customer's first view moves a card here. */}
                                    {stage.id === 'viewed' ? 'Moves here when the customer opens it' : 'Drag one here'}
                                </p>
                            )}
                        </div>
                        {more > 0 && (
                            <div className="pt-1.5 text-center">
                                <button
                                    type="button"
                                    onClick={() => setRevealed((m) => ({ ...m, [stage.id]: (m[stage.id] || 0) + CLOSED_PAGE }))}
                                    className="text-[11px] font-semibold text-blue-700 hover:underline"
                                >
                                    Show {Math.min(CLOSED_PAGE, more)} more
                                </button>
                                <p className="text-[10px] text-gray-500">showing {body.length} of {count}</p>
                            </div>
                        )}
                    </>
                ) : (
                    // D1: a collapsed closed column is still a drop target — you
                    // can drag onto the strip without expanding it first.
                    <button
                        type="button"
                        onClick={() => setRevealed((m) => ({ ...m, [stage.id]: CLOSED_PAGE }))}
                        className="w-full min-h-[80px] rounded-md border border-dashed border-gray-300 text-[11px] text-gray-400 hover:text-gray-600 hover:border-gray-400"
                    >
                        {count === 0 ? 'Empty' : `Show ${count}`}
                    </button>
                )}
            </div>
        );
    };

    return (
        <div>
            {/* D2(c): a card that moved itself says so. Non-blocking, dismissible,
                newest first, and capped so a burst can't take the screen. */}
            {autoNotes.length > 0 && (
                <div className="mb-3 space-y-1">
                    {autoNotes.map((n) => (
                        <div key={n.key} className="flex items-start justify-between gap-2 px-3 py-2 rounded-md bg-blue-50 border border-blue-200 text-xs text-blue-800">
                            <span>{n.text}</span>
                            <button
                                type="button"
                                onClick={() => setAutoNotes((notes) => notes.filter((x) => x.key !== n.key))}
                                className="shrink-0 text-blue-400 hover:text-blue-700"
                                aria-label="Dismiss"
                            >
                                <X size={12} />
                            </button>
                        </div>
                    ))}
                </div>
            )}

            {/* qclose-3e8a5c17 — every refusal is ALSO stated here, above the
                board, in the server's own words — so it is visible wherever the
                drag left the horizontal scroll. */}
            {refusal && (
                <div className="mb-3 flex items-start justify-between gap-2 px-3 py-2 rounded-md bg-red-50 border border-red-200 text-xs text-red-800" role="alert">
                    <span>
                        <span className="font-semibold">{refusal.quoteNumber || `That ${term.word.toLowerCase()}`} didn&apos;t move:</span>{' '}
                        {refusal.message}
                    </span>
                    <button
                        type="button"
                        onClick={() => setRefusal(null)}
                        className="shrink-0 text-red-400 hover:text-red-700"
                        aria-label="Dismiss"
                    >
                        <X size={12} />
                    </button>
                </div>
            )}

            {!canMove && (
                <p className="mb-2 flex items-center gap-1.5 text-xs text-gray-500">
                    <Lock size={12} /> You can see the board but not move {term.wordPlural.toLowerCase()} between stages.
                </p>
            )}

            {/* qboard-7e3c9b42-autoscroll — the drag auto-scroller reads the
                pointer here rather than on each column, so it keeps working
                over the gaps between columns and over the strip's own padding.
                No preventDefault: this listener only measures. */}
            <div
                ref={scrollerRef}
                onDragOver={handleBoardDragOver}
                className="flex gap-3 overflow-x-auto pb-4 items-start"
            >
                {columns.map(renderColumn)}

                {/* Defensive, and read-only: an estimate with no stage, or one
                    pointing at a stage this org no longer has, would otherwise
                    vanish from the board entirely. Zero such estimates exist in
                    production today (all 145 are staged), so this column is
                    normally absent. It is NOT a drop target — there is no stage
                    to move them to that isn't already a column above. */}
                {unplaced.count > 0 && (
                    <div className="shrink-0 w-72 rounded-lg border border-dashed border-gray-300 bg-gray-50 p-2.5">
                        <h3 className="font-bold text-sm text-gray-700 leading-tight">Not placed</h3>
                        <p className="mt-0.5 text-[11px] font-semibold text-gray-500">
                            {unplaced.count} · {formatCurrency(unplaced.total)}
                        </p>
                        <p className="mt-0.5 text-[10px] text-gray-400">No stage, or a stage that was removed</p>
                        <div
                            data-board-col-body=""
                            className={`mt-2.5 space-y-2 overflow-y-auto overflow-x-hidden ${COLUMN_BODY_MAX} -mx-0.5 px-0.5`}
                        >
                            {unplaced.cards.map((q) => renderCard(q, null))}
                        </div>
                    </div>
                )}
            </div>

            <CloseDetailsModal
                isOpen={!!closePrompt}
                onClose={() => setClosePrompt(null)}
                quote={closePrompt?.quote || null}
                outcome={closePrompt?.outcome || null}
                closeActivityId={closePrompt?.closeActivityId || null}
                vocabularies={vocabularies}
                docWord={term.word}
            />

            {quotes.length === 0 ? (
                <div className="text-center py-10 bg-gray-50 rounded-lg border border-dashed border-gray-300">
                    <p className="text-gray-500">No {term.wordPlural.toLowerCase()} match the current filters.</p>
                </div>
            ) : (
                <p className="text-xs text-gray-400">
                    {quotes.length} {quotes.length === 1 ? term.word.toLowerCase() : term.wordPlural.toLowerCase()} on the board · {formatCurrency(boardTotal)} total
                </p>
            )}
        </div>
    );
};

export default QuotePipelineBoard;
